import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Skeleton,
  TextField,
  Typography,
  debounce,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Pessoa } from "../../../interfaces";
import { consultaWebhook, webhookAtualizaDados } from "../../../services/api";
import { formatarCpfCnpj } from "../../../utils/formataCpfCnpj";
import { isMunicipio, MUNICIPIOS_DIALOG_SINCRONIZA_PESSOA } from "../../../constants/municipios";

type PessoaResumo = Pick<Pessoa, "id" | "nome" | "cpf_cnpj" | "codigo"> | null;

export function DialogSincronizaPessoa(props: {
  municipio: string;
  openDialog: boolean;
  handleCloseDialog: () => void;
}) {
  const [param, setParam] = useState("");
  const [loading, setLoading] = useState(false);
  const [pessoa, setPessoa] = useState<PessoaResumo>(null);
  const [pessoaFetched, setPessoaFetched] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const isBuscaCodigo = isMunicipio(props.municipio, MUNICIPIOS_DIALOG_SINCRONIZA_PESSOA);

  useEffect(() => {
    if (props.openDialog) {
      setParam("");
      setPessoa(null);
      setPessoaFetched(false);
      setLoading(false);
    } else {
      setParam("");
      setPessoa(null);
      setPessoaFetched(false);
      setLoading(false);
    }
  }, [props.openDialog]);

  const getPessoaCodigo = useCallback(async (codigo: string) => {
    const data = await consultaWebhook("pessoa", { codigo });

    const normalized: PessoaResumo =
      data && (data?.codigo || data?.id || data?.nome)
        ? ({
            id: data?.id,
            nome: data?.nome,
            cpf_cnpj: data?.cpf_cnpj ?? data?.cpfCnpj ?? data?.documento,
            codigo: data?.codigo ?? codigo,
          } as PessoaResumo)
        : null;

    if (normalized?.codigo) return normalized;
    return null;
  }, []);

  useEffect(() => {
    const q = param.trim();
    let active = true;

    if (!q) {
      setPessoa(null);
      setPessoaFetched(false);
      return;
    }

    setLoading(true);
    getPessoaCodigo(q)
      .then((res) => {
        if (!active) return;
        setPessoa(res);
        setPessoaFetched(true);
      })
      .finally(() => active && setLoading(false));

    return () => {
      active = false;
    };
  }, [param, getPessoaCodigo]);

  const handleChangeWithDebounce = useMemo(
    () =>
      debounce((value: string) => {
        setParam(value);
      }, 200),
    []
  );

  async function handleSincronizaPessoa() {
    const codigo = param.trim();
    setSyncing(true);

    try {
      const result = await webhookAtualizaDados("pessoa", { codigo });
      if (result) {
        toast.success("Informações da pessoa atualizadas com sucesso.");
        props.handleCloseDialog();
      } else {
        toast.error("Falha ao atualizar as informações da pessoa.");
      }
    } catch (error) {
      toast.error("Erro ao sincronizar a pessoa.");
      console.error(error);
    } finally {
      setSyncing(false);
    }
  }

  const searchLabel = isBuscaCodigo
    ? "Busque pelo código da pessoa"
    : "Busque pelo nome, CPF ou CNPJ da pessoa";

  const disableSync = isBuscaCodigo
    ? param.trim().length === 0 || (!loading && pessoaFetched && !pessoa)
    : false;

  return (
    <Dialog open={props.openDialog} onClose={props.handleCloseDialog}>
      <DialogContent>
        <h2 style={{ textAlign: "center" }}>
          Selecione uma pessoa para vincular ao imobiliário
        </h2>

        <TextField
          fullWidth
          variant="standard"
          label={searchLabel}
          size="small"
          value={param}
          onChange={(e) => handleChangeWithDebounce((e.target.value || "").toString())}
          inputProps={{ maxLength: 50 }}
        />

        {loading && <Skeleton height={29} />}

        {!loading && pessoaFetched && pessoa && (
          <div style={{ marginTop: 12 }}>
            <Typography variant="body1" display="block">
              <b>Nome:</b> {pessoa.nome || "Não informado"}
            </Typography>
            <Typography variant="body1" display="block">
              <b>CPF/CNPJ:</b> {formatarCpfCnpj(pessoa.cpf_cnpj) || "Não informado"}
            </Typography>
          </div>
        )}

        {!loading && pessoaFetched && !pessoa && param.trim().length > 0 && (
          <Typography color="error" sx={{ mt: 1 }}>
            Código não localizado: <b>{param.trim()}</b>
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
        <Button
          color="success"
          onClick={handleSincronizaPessoa}
          variant="contained"
          disabled={disableSync || syncing}
        >
          {syncing ? "SINCRONIZANDO..." : "SINCRONIZAR PESSOA"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

